import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { useToast } from "../../hooks/use-toast";
import { 
  CreditCard, 
  DollarSign, 
  Phone, 
  Mail,
  Save,
  Key,
  Settings,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { mockPaymentMethods, mockApi } from "../../mock";

const PaymentSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Load real payment methods from backend on mount
  useEffect(() => {
    mockApi.fetchPaymentMethods().then(methods => {
      // Normalize: backend uses isEnabled, frontend uses enabled
      const normalized = (methods || []).map(m => ({
        ...m,
        enabled: m.isEnabled !== undefined ? m.isEnabled : m.enabled,
      }));
      setPaymentMethods(normalized);
    });
  }, []);

  const handleToggleMethod = async (id) => {
    setLoading(true);
    const method = paymentMethods.find(m => m.id === id);
    const newEnabled = !method.enabled;
    try {
      const r = await mockApi.updatePaymentSettings(id, { isEnabled: newEnabled, status: newEnabled ? "active" : "inactive" });
      if (r.success) {
        setPaymentMethods(paymentMethods.map(m => m.id === id ? { ...m, enabled: newEnabled, isEnabled: newEnabled, status: newEnabled ? "active" : "inactive" } : m));
        toast({ title: "Payment method updated", description: "Saved to backend" });
      } else {
        toast({ title: "Update failed", description: "Backend rejected", variant: "destructive" });
      }
    } finally { setLoading(false); }
  };

  const handleUpdateCredentials = async (id, field, value) => {
    setPaymentMethods(paymentMethods.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      // Save each method individually to its endpoint
      const results = await Promise.all(
        paymentMethods.map(m => mockApi.updatePaymentSettings(m.id, m))
      );
      const allOk = results.every(r => r.success);
      if (allOk) {
        toast({ title: "Settings saved", description: "All payment configurations saved to backend" });
      } else {
        toast({ title: "Partial save", description: "Some methods failed", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Save failed", description: error.message || "Network error", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const getPaymentIcon = (name) => {
    switch (name.toLowerCase()) {
      case 'stripe':
        return <CreditCard className="h-6 w-6 text-purple-600" />;
      case 'paypal':
        return <DollarSign className="h-6 w-6 text-blue-600" />;
      case 'square':
        return <CreditCard className="h-6 w-6 text-green-600" />;
      case 'zelle':
        return <Phone className="h-6 w-6 text-indigo-600" />;
      default:
        return <CreditCard className="h-6 w-6 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-emerald-600" />
            <span>Payment Gateway Configuration</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            Configure your payment methods to accept customer payments. Enable multiple options to provide flexibility for your customers.
          </p>

          <div className="grid lg:grid-cols-2 gap-6">
            {paymentMethods.map((method) => (
              <Card key={method.id} className="border-2">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getPaymentIcon(method.name)}
                      <div>
                        <h3 className="font-semibold">{method.name}</h3>
                        <Badge 
                          variant={method.status === "active" ? "default" : "secondary"}
                          className="text-xs mt-1"
                        >
                          {method.status}
                        </Badge>
                      </div>
                    </div>
                    <Switch
                      checked={method.enabled}
                      onCheckedChange={() => handleToggleMethod(method.id)}
                      disabled={loading}
                    />
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {method.name === "Stripe" && (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor={`stripe-key-${method.id}`} className="text-sm">
                          Secret Key
                        </Label>
                        <div className="relative">
                          <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id={`stripe-key-${method.id}`}
                            type="password"
                            placeholder="sk_test_..."
                            value={method.apiKey || ""}
                            onChange={(e) => handleUpdateCredentials(method.id, "apiKey", e.target.value)}
                            className="pl-10"
                            disabled={!method.enabled}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Get your API key from <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe Dashboard</a>
                      </p>
                    </div>
                  )}

                  {method.name === "PayPal" && (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor={`paypal-client-${method.id}`} className="text-sm">
                          Client ID
                        </Label>
                        <div className="relative">
                          <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id={`paypal-client-${method.id}`}
                            placeholder="PayPal Client ID"
                            value={method.clientId || ""}
                            onChange={(e) => handleUpdateCredentials(method.id, "clientId", e.target.value)}
                            className="pl-10"
                            disabled={!method.enabled}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Get your Client ID from <a href="https://developer.paypal.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">PayPal Developer</a>
                      </p>
                    </div>
                  )}

                  {method.name === "Square" && (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor={`square-key-${method.id}`} className="text-sm">
                          Application ID
                        </Label>
                        <div className="relative">
                          <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id={`square-key-${method.id}`}
                            placeholder="Square Application ID"
                            value={method.apiKey || ""}
                            onChange={(e) => handleUpdateCredentials(method.id, "apiKey", e.target.value)}
                            className="pl-10"
                            disabled={!method.enabled}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Get your Application ID from <a href="https://developer.squareup.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Square Developer</a>
                      </p>
                    </div>
                  )}

                  {method.name === "Zelle" && (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor={`zelle-phone-${method.id}`} className="text-sm">
                          Phone Number
                        </Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id={`zelle-phone-${method.id}`}
                            placeholder="+1-555-0123"
                            value={method.phoneNumber || ""}
                            onChange={(e) => handleUpdateCredentials(method.id, "phoneNumber", e.target.value)}
                            className="pl-10"
                            disabled={!method.enabled}
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor={`zelle-email-${method.id}`} className="text-sm">
                          Email Address
                        </Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id={`zelle-email-${method.id}`}
                            type="email"
                            placeholder="business@example.com"
                            value={method.email || ""}
                            onChange={(e) => handleUpdateCredentials(method.id, "email", e.target.value)}
                            className="pl-10"
                            disabled={!method.enabled}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Customers will send Zelle payments to this phone number or email
                      </p>
                    </div>
                  )}

                  {method.enabled && method.status === "active" && (
                    <div className="flex items-center space-x-2 p-2 bg-green-50 rounded-lg">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-800">Ready to accept payments</span>
                    </div>
                  )}

                  {method.enabled && method.status === "inactive" && (
                    <div className="flex items-center space-x-2 p-2 bg-yellow-50 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm text-yellow-800">Configuration required</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="pt-6 border-t">
            <Button
              onClick={saveSettings}
              disabled={loading}
              className="w-full bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700"
            >
              {loading ? (
                "Saving..."
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Payment Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payment Testing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            <span>Payment Testing</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Test Your Payment Setup</h4>
            <p className="text-sm text-blue-700 mb-4">
              Use these test cards to verify your payment integration is working correctly:
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium">Stripe Test Cards:</p>
                <p>4242424242424242 (Visa)</p>
                <p>5555555555554444 (Mastercard)</p>
              </div>
              <div>
                <p className="font-medium">PayPal Sandbox:</p>
                <p>Use PayPal sandbox accounts</p>
                <p>for testing transactions</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSettings;